import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { makeRequest2 } from '../../../utils/makeRequest';
import { toastify } from '../../../utils/toast';
import { getItem } from '../../../utils/localStorage';
import Layout from '../component/layout';
import { useFacilityStore } from '../../../app/z-store/store';

const AUDIT_URL = '/api/app/power_meter_management/controllers/audit_logs';

const MODULES = [
    { label: 'All modules', value: '' },
    { label: 'Power Credit', value: 'power_credit' },
    { label: 'Power Meters', value: 'power_meter' },
    { label: 'Power Meter Commands', value: 'power_meter_command' },
    { label: 'Power Charges', value: 'power_charge' },
    { label: 'Power Settings', value: 'power_settings' },
    { label: 'Power Accounts', value: 'power_account' },
    { label: 'Power Billing', value: 'power_billing' },
    { label: 'Cash Payments', value: 'cash_payment' },
    { label: 'Payments', value: 'payment' },
    { label: 'Credit/Debit Notes & Overpayments', value: 'finance_adjustment' },
    { label: 'Invoices', value: 'invoice' },
    { label: 'Finance Settings', value: 'finance_settings' },
    { label: 'Levy', value: 'levy' },
    { label: 'Levy Deposits', value: 'levy_deposit' },
    { label: 'Lease', value: 'lease' },
    { label: 'Property Management', value: 'property_management' },
    { label: 'Unit Assignment', value: 'unit_assignment' },
    { label: 'Meter Settings', value: 'meter_settings' },
    { label: 'Water Meters', value: 'water_meter' },
    { label: 'Water Analog Meters', value: 'water_analog_meter' },
    { label: 'Water Accounts', value: 'water_account' },
    { label: 'Water Billing', value: 'water_billing' },
    { label: 'Common Area Readings', value: 'common_area_reading' },
];
const ACTIONS = ['', 'CREATE', 'UPDATE', 'DELETE'];
const STATUSES = ['', 'SUCCESS', 'FAILED'];
const PRESETS = [
    { label: 'Last hour', ms: 60 * 60 * 1000 },
    { label: 'Today', ms: 'today' },
    { label: '7 days', ms: 7 * 24 * 60 * 60 * 1000 },
    { label: '30 days', ms: 30 * 24 * 60 * 60 * 1000 },
];

const toLocalInput = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const defaultRange = () => {
    // Empty "to" means "now" on the server, so records saved after the page loaded still show up
    return { from: toLocalInput(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)), to: '' };
};

function AuditLogs() {
    const facilityId = useFacilityStore((state) => state.facilityId);
    const [allowed, setAllowed] = useState(null);
    const [logs, setLogs] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [lazy, setLazy] = useState({ first: 0, rows: 20, sortField: 'createdAt', sortOrder: -1 });
    const [filters, setFilters] = useState({
        module: '', action: '', status: '', actor: '', reference: '', accountNumber: '', ...defaultRange(),
    });
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        (async () => {
            const appUser = await getItem('APPUSER');
            setAllowed(appUser?.user?.role === 'ninja');
        })();
    }, []);

    const fetchLogs = useCallback(async (f, l) => {
        if (!facilityId) return;
        setLoading(true);
        try {
            const q = new URLSearchParams({
                page: String(Math.floor(l.first / l.rows) + 1),
                limit: String(l.rows),
                sortBy: l.sortField || 'createdAt',
                sortOrder: l.sortOrder === 1 ? 'asc' : 'desc',
                facilityId,
            });
            ['module', 'action', 'status', 'actor', 'reference', 'accountNumber'].forEach((k) => {
                if (f[k] && String(f[k]).trim()) q.set(k, String(f[k]).trim());
            });
            if (f.from) q.set('from', new Date(f.from).toISOString());
            if (f.to) q.set('to', new Date(f.to).toISOString());

            const res = await makeRequest2(`${AUDIT_URL}?${q.toString()}`, 'GET');
            if (res.success) {
                setLogs(res.data?.data?.logs || []);
                setTotalRecords(res.data?.data?.totalCount || 0);
            } else {
                toastify(res.error || 'Failed to fetch audit logs', 'error');
            }
        } catch (e) {
            toastify('Error fetching audit logs', 'error');
        } finally {
            setLoading(false);
        }
    }, [facilityId]);

    // Refetch on page / sort change (filters apply on Search)
    useEffect(() => {
        if (allowed) fetchLogs(filters, lazy);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowed, lazy, facilityId]);

    const setF = (k) => (e) => setFilters((p) => ({ ...p, [k]: e.target.value }));

    const search = () => {
        const next = { ...lazy, first: 0 };
        setLazy(next); // triggers fetch via effect
        if (lazy.first === 0) fetchLogs(filters, next);
    };

    const reset = () => {
        const f = { module: '', action: '', status: '', actor: '', reference: '', accountNumber: '', ...defaultRange() };
        const l = { first: 0, rows: 20, sortField: 'createdAt', sortOrder: -1 };
        setFilters(f);
        setLazy(l);
        if (lazy.first === 0 && lazy.sortField === 'createdAt' && lazy.sortOrder === -1) fetchLogs(f, l);
    };

    const applyPreset = (p) => {
        const to = new Date();
        let from;
        if (p.ms === 'today') { from = new Date(); from.setHours(0, 0, 0, 0); }
        else from = new Date(to.getTime() - p.ms);
        setFilters((prev) => ({ ...prev, from: toLocalInput(from), to: '' }));
    };

    const openDetails = async (row) => {
        setSelected({ ...row, loadingDetails: true });
        const res = await makeRequest2(`${AUDIT_URL}/${row._id}?facilityId=${facilityId}`, 'GET');
        if (res.success) setSelected(res.data?.data);
        else {
            toastify(res.error || 'Failed to load details', 'error');
            setSelected(null);
        }
    };

    if (allowed === null) return <Layout />;
    if (!allowed) {
        return (
            <Layout>
                <div className="container-fluid pt-4"><div className="alert alert-danger">Access denied.</div></div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <ul className="breadcrumb">
                        <li className="breadcrumb-item"><Link to="/facility/">Dashboard</Link></li>
                        <li className="breadcrumb-item">Audit Logs</li>
                    </ul>
                </div>
            </div>

            <div className="card">
                <div className="card-header"><h3 className="mb-0">Audit Logs</h3></div>
                <div className="card-body">
                    <div className="row g-2 mb-2">
                        <div className="col-md-3">
                            <label className="form-label small mb-1">From</label>
                            <input type="datetime-local" className="form-control" value={filters.from} onChange={setF('from')} />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label small mb-1">To</label>
                            <input type="datetime-local" className="form-control" value={filters.to} onChange={setF('to')} />
                        </div>
                        <div className="col-md-6 d-flex align-items-end gap-2 flex-wrap">
                            {PRESETS.map((p) => (
                                <button key={p.label} className="btn btn-sm btn-outline-secondary" onClick={() => applyPreset(p)}>{p.label}</button>
                            ))}
                        </div>
                    </div>
                    <div className="row g-2 mb-3">
                        <div className="col-md-2">
                            <select className="form-select" value={filters.module} onChange={setF('module')}>
                                {MODULES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <select className="form-select" value={filters.action} onChange={setF('action')}>
                                {ACTIONS.map((a) => <option key={a} value={a}>{a || 'All actions'}</option>)}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <select className="form-select" value={filters.status} onChange={setF('status')}>
                                {STATUSES.map((s) => <option key={s} value={s}>{s || 'All statuses'}</option>)}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <input className="form-control" placeholder="Performed by" value={filters.actor} onChange={setF('actor')} onKeyDown={(e) => e.key === 'Enter' && search()} />
                        </div>
                        <div className="col-md-2">
                            <input className="form-control" placeholder="Reference" value={filters.reference} onChange={setF('reference')} onKeyDown={(e) => e.key === 'Enter' && search()} />
                        </div>
                        <div className="col-md-2">
                            <input className="form-control" placeholder="Account no." value={filters.accountNumber} onChange={setF('accountNumber')} onKeyDown={(e) => e.key === 'Enter' && search()} />
                        </div>
                    </div>
                    <div className="d-flex gap-2 mb-3">
                        <button className="btn btn-primary" onClick={search}>Search</button>
                        <button className="btn btn-outline-secondary" onClick={reset}>Reset</button>
                    </div>

                    <div className="dt-responsive table-responsive">
                        <DataTable
                            value={logs}
                            lazy
                            paginator
                            rows={lazy.rows}
                            first={lazy.first}
                            totalRecords={totalRecords}
                            rowsPerPageOptions={[20, 50, 100]}
                            onPage={(e) => setLazy((p) => ({ ...p, first: e.first, rows: e.rows }))}
                            sortField={lazy.sortField}
                            sortOrder={lazy.sortOrder}
                            onSort={(e) => setLazy((p) => ({ ...p, first: 0, sortField: e.sortField, sortOrder: e.sortOrder }))}
                            loading={loading}
                            stripedRows
                            emptyMessage="No audit records found for this filter."
                            tableStyle={{ minWidth: '70rem' }}
                        >
                            <Column field="createdAt" header="When" sortable body={(r) => new Date(r.createdAt).toLocaleString()} />
                            <Column field="actor" header="Performed By" sortable body={(r) => (
                                <div>
                                    <div>{r.actor?.fullName || 'Unknown'}</div>
                                    <small className="text-muted">{r.actor?.email} {r.actor?.role ? `(${r.actor.role})` : ''}</small>
                                </div>
                            )} />
                            <Column field="module" header="Module" sortable />
                            <Column field="action" header="Action" sortable />
                            <Column field="reference" header="Reference" sortable />
                            <Column field="accountNumber" header="Account" />
                            <Column field="description" header="Description" />
                            <Column field="status" header="Status" sortable body={(r) => (
                                <span className={`badge ${r.status === 'SUCCESS' ? 'bg-success' : 'bg-danger'} text-light`}>{r.status}</span>
                            )} />
                            <Column header="" body={(r) => (
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => openDetails(r)}>Details</button>
                            )} />
                        </DataTable>
                    </div>
                </div>
            </div>

            <Dialog header="Audit Details" visible={!!selected} style={{ width: '650px' }} onHide={() => setSelected(null)} modal>
                {selected?.loadingDetails
                    ? <div>Loading...</div>
                    : selected && <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(selected, null, 2)}</pre>}
            </Dialog>
        </Layout>
    );
}

export default AuditLogs;
